import { get, post } from '../../tools/http';

const client = {
    getMeetings(groupId) {
        return get('profile', `meetings/${groupId}`);
    },
    getEvents(groupId) {
        return get('profile', `events?group_id=${groupId}`);
    },
    getCmsEntity(entity, {query}) {
        return get('cms', `entity/select/${entity}${query || ''}`);
    },
    getCmsEntities(entity, ids) {
        if (ids.length === 0) {
            return [];
        }
        let query = '?' + ids.map(v => `id[]=${v}`).join('&');
        return this.getCmsEntity(entity, { query });
    },
    createMeeting(data) {
        return post('profile', 'meeting/create', data, true);
    },
    updateMeeting(meetingId, status, datetime = null) {
        let updates = {
            meeting_id: meetingId,
            status: status,
        };
        if (datetime !== null) {
            updates.datetime = datetime;
        }
        return post('profile', 'meeting/update', updates, true);
    },
    rejectMeeting(data) {
        return post('profile', 'meeting/reject', data, true);
    },
    setEventStatus(eventId, status) {
        return post('profile', `event/${eventId}/status/${status}`);
    },
    rateMeeting(data) {
        return post('profile', 'meeting/rating', data, true);
    },
};

const ActivityType = Object.freeze({
    webinar: 'webinar',
    meeting: 'meeting',
    session: 'session',
    course: 'course',
});

const ActivityStatus = Object.freeze({
    confirmation: 'confirmation',
    in_progress: 'in_progress',
    rejected: 'rejected',
    completed: 'completed',
    missed: 'missed',
});

class MeetingStatus {
    static activityStatusToString(status) {
        const _info = {
            [ActivityStatus.confirmation]: 'as_agreed',
            [ActivityStatus.in_progress]: 'accepted',
            [ActivityStatus.rejected]: 'rejected',
            [ActivityStatus.completed]: 'completed',
            [ActivityStatus.missed]: 'missed',
        };
        return _info[status];
    }

    static  stringToActivityStatus(name) {
        const _info = {
            'as_agreed': ActivityStatus.confirmation,
            'accepted': ActivityStatus.in_progress,
            'rejected': ActivityStatus.rejected,
            'completed': ActivityStatus.completed,
            'missed': ActivityStatus.missed,
        };
        return _info[name] || null;
    }
}

class EventStatus {
    static activityStatusToString(status) {
        const _info = {
            [ActivityStatus.confirmation]: null,
            [ActivityStatus.in_progress]: null,
            [ActivityStatus.completed]: 'done',
            [ActivityStatus.missed]: 'skipped',
            [ActivityStatus.rejected]: null,
        };
        return _info[status];
    }

    static stringToActivityStatus(name) {
        const _info = {
            'done': ActivityStatus.completed,
            'skipped': ActivityStatus.missed,
        };
        return _info[name] || ActivityStatus.in_progress;
    }
}

const ActivityFactory = {
    create: ({ id, type, foreignEventId, status, link, theme, description, datetime, location, rating = null }) => ({
        id, type, foreignEventId, status, link, theme, description, datetime, location, rating,
    }),
    fromEvent(event, cmsItem) {
        let link = null;
        if (cmsItem.foreignWebinarId) {
            link = `https://lms.${window.rsvDomain}/webinars/${cmsItem.foreignWebinarId}/page`;
        }
        if (cmsItem.foreignCourseId) {
            link = `https://lms.${window.rsvDomain}/courses/${cmsItem.foreignCourseId}/page`;
        }

        return this.create({
            ...event,
            status: EventStatus.stringToActivityStatus(event.status),
            link,
            theme: cmsItem.theme,
            description: cmsItem.description,
            datetime: new Date(event.dateStart),
            location: cmsItem.location,
        });
    },
    fromMeeting(meeting, role) {
        let status = MeetingStatus.stringToActivityStatus(meeting.status);
        let datetime = new Date(meeting.datetime);
        let rating = role === 'student'
            ? meeting.ratingFromStudent
            : meeting.ratingFromLeader;

        if (status !== ActivityStatus.rejected &&
            status !== ActivityStatus.confirmation) {

            if (rating == null) {
                status = ActivityStatus.in_progress;
            } else if (rating === 0) {
                status = ActivityStatus.missed;
            } else {
                status = ActivityStatus.completed;
            }
        }

        // let canRate = [ActivityStatus.in_progress,
        //         ActivityStatus.missed,
        //         ActivityStatus.completed
        //     ].indexOf(status) > -1 && datetime < new Date();

        return this.create({
            ...meeting,
            type: ActivityType.meeting,
            status,
            datetime,
            link: null,
            rating,
        });
    },
};

export { ActivityStatus, ActivityType };

export default {
    namespaced: true,
    state: {
        events: [],
        meetings: [],
    },
    mutations: {
        setEvents: (state, items) => state.events = items,
        setMeetings: (state, items) => state.meetings = items,
    },
    actions: {
        async loadActivities(ctx) {
            ctx.dispatch('loadMeetings');
            ctx.dispatch('loadEvents');
        },
        async loadMeetings(ctx) {
            let groupId = ctx.rootState.mentoring.groupId;
            if (groupId) {
                let meetings = (await client.getMeetings(groupId)).meetings;
                ctx.commit('setMeetings', meetings.map(v => ActivityFactory.fromMeeting(v, ctx.rootState.mentoring.role)));
            }
        },
        async loadEvents(ctx) {
            let groupId = ctx.rootState.mentoring.groupId;
            if (groupId) {
                let events = (await client.getEvents(groupId)).events;

                // loading cms items
                let cmsItems = (await Promise.all(
                    Object
                        .entries(events.reduce((acc, v) => {
                            acc[v.type] = acc[v.type] || [];
                            acc[v.type].push(v.foreignEventId);
                            return acc;
                        }, {}))
                        .map(async ([type, ids]) => ({
                            type,
                            items: await client
                                .getCmsEntities(type, ids),
                        }))
                )).reduce((acc, v) => {
                    acc[v.type] = v.items.list
                        .reduce((acc, v) => {
                            acc[v.id] = v;
                            return acc;
                        }, {});
                    return acc;
                }, {});

                ctx.commit('setEvents', events.map(v => ActivityFactory
                    .fromEvent(v, cmsItems[v.type][v.foreignEventId]))
                    .map(v => {
                        v.initiatorAvatar =  ctx.rootState.mentoring.pair.avatar;
                        return v;
                    })
                );
            }
        },
        async createMeeting(ctx, item) {
            let groupId = ctx.rootState.mentoring.groupId;
            if (groupId) {
                await client.createMeeting({ ...item, group_id: groupId});
                ctx.dispatch('loadMeetings');
            }
        },
        async rejectMeeting(ctx, {meeting_id, rejection_reason}) {
            await client.rejectMeeting({
                meeting_id,
                rejection_reason,
            });
            ctx.dispatch('loadMeetings');
        },
        async rateMeeting(ctx, {meeting_id, rating}) {
            await client.rateMeeting({
                meeting_id,
                rating,
            });
            await ctx.dispatch('loadMeetings');
        },
        updateActivityStatus(ctx, {item, status, datetime = null}) {
            let type = (item.type === ActivityType.meeting)
                ? 'Meeting'
                : 'Event';
            return ctx.dispatch(`update${type}Status`, {item, status, datetime});
        },
        async updateMeetingStatus(ctx, {item, status, datetime}) {
            const statusString = MeetingStatus.activityStatusToString(status);
            if (status !== ActivityStatus.in_progress &&
                status !== ActivityStatus.completed &&
                status !== ActivityStatus.missed) {
                throw new Error(
                    `Unable to change activity status to ${statusString} by user request`);
            }
            await client.updateMeeting(
                item.id,
                statusString,
                datetime ? datetime.toISOString() : null
            );
            ctx.dispatch('loadMeetings');
            item.status = status;
            return item;
        },
        async updateEventStatus(ctx, {item, status}) {
            const statusString = EventStatus.activityStatusToString(status);
            if (statusString == null) {
                throw new Error(
                    `Unable to change activity status to ${statusString} by user request`);
            }
            await client.setEventStatus(item.id, statusString);
            ctx.dispatch('loadEvents');
            item.status = status;
            return item;
        },
    },
    getters: {
        getActivity: (state) => ({ id, type }) =>  [...state.events, ...state.meetings]
            .find(v => v.id === id && v.type === type) || null,

        upcomingActivities: state => [
            ...[...state.events, ...state.meetings]
                .filter((activity) => activity.status === ActivityStatus.confirmation)
                .sort((a, b) => b.datetime.getTime() - a.datetime.getTime()),
            ...[...state.events, ...state.meetings]
                .filter((activity) =>
                    (activity.status === ActivityStatus.in_progress) &&
                    activity.datetime != null &&
                    activity.datetime > new Date())
                .sort((a, b) => a.datetime.getTime() - b.datetime.getTime()),
        ],

        // currentActivities: state => [...state.events, ...state.meetings]
        //     .filter((activity) => activity.status === ActivityStatus.in_progress)
        //     .sort((a, b) => a.datetime.getTime() - b.datetime.getTime()),

        pastActivities: state => [...state.events, ...state.meetings]
            .filter((activity) =>
                (activity.status === ActivityStatus.in_progress ||
                    activity.status === ActivityStatus.completed) &&
                activity.datetime != null &&
                activity.datetime < new Date())
            .sort((a, b) => b.datetime.getTime() - a.datetime.getTime()),


        // doneActivities: state => [...state.events, ...state.meetings]
        //     .filter(v => v.status === ActivityStatus.completed)
        //     .sort((a, b) => b.datetime.getTime() - a.datetime.getTime()),

        skippedActivities: state => [...state.events, ...state.meetings]
            .filter((activity) => activity.status === ActivityStatus.missed)
            .sort((a, b) => b.datetime.getTime() - a.datetime.getTime()),
    },
};